// Logistics-specific status / booking-state → worded tag. The OMS-wide StatusTag maps the fulfilment
// vocabulary; logistics has its own (created → delivered lifecycle + booking states). These render as
// a WORDED tag, not a colour-only dot: the console is read by non-technical ops who scan a column of
// fifty rows, and a colour whose meaning lives in a tooltip cannot be scanned (or hovered on touch).
import { Tag, Tooltip } from "antd";
import React from "react";

// Tag `color` takes preset names (blue/cyan/green/red/gold/…). Grey states use the default tag.
const STATUS_META: Record<string, { color?: string; label: string }> = {
  created: { label: "Not dispatched" },
  dispatched: { color: "blue", label: "Dispatched" },
  in_transit: { color: "blue", label: "In transit" },
  out_for_delivery: { color: "cyan", label: "Out for delivery" },
  delivered: { color: "green", label: "Delivered" },
  received: { color: "green", label: "Received" },
  cancelled: { color: "red", label: "Cancelled" },
};

// Booking lifecycle (LogisticsDocument.lifecycle). DRAFT = booking exists but not yet live (carrier
// call mid-flight, or a manual booking awaiting the ops team's confirmation); ACTIVE = live.
const STATE_META: Record<string, { color?: string; label: string; help?: string }> = {
  draft: {
    color: "gold",
    label: "Not booked yet",
    help: "Not yet with the carrier — either the booking call is still going through, or someone has to confirm it in Pending Tasks.",
  },
  active: { color: "green", label: "Booked", help: "The carrier has accepted this truck." },
  cancelled: { color: "red", label: "Cancelled" },
  short_closed: { color: "gold", label: "Closed short", help: "Closed before everything moved." },
  superseded: { label: "Replaced", help: "A newer booking took over from this one." },
};

// The clean, operator-facing delivery signal (backend DeliveryStatus) — the ONE tag procurement
// leads with. created → booked → in_transit → delivered / cancelled. The finer axes above
// (physical status, lifecycle) stay available for the drawer's detailed trail, but the board shows
// only this so an operator scanning fifty rows reads one unambiguous word per booking.
const DELIVERY_META: Record<string, { color?: string; label: string; help?: string }> = {
  created: {
    label: "Created",
    help: "The move exists / its info reached the carrier — nothing has been booked yet.",
  },
  booked: {
    color: "blue",
    label: "Booked",
    help: "A vehicle was assigned, or a TMS trip was planned for it.",
  },
  in_transit: { color: "cyan", label: "In transit", help: "Picked up and on its way." },
  attempted: {
    color: "orange",
    label: "Attempted",
    help: "A delivery was tried and failed — the goods came back. Reattempt it.",
  },
  delivered: { color: "green", label: "Delivered", help: "Arrived at the destination." },
  cancelled: { color: "red", label: "Cancelled" },
};

const humanize = (v?: string | null) => (v ? v.replace(/_/g, " ") : "—");

const WordTag: React.FC<{
  value?: string | null;
  meta?: { color?: string; label: string; help?: string };
}> = ({ value, meta }) => {
  if (!value) return <Tag>—</Tag>;
  const label = meta?.label ?? humanize(value);
  const tag = (
    <Tag color={meta?.color} style={{ marginInlineEnd: 0 }}>
      {label}
    </Tag>
  );
  return meta?.help ? (
    <Tooltip title={meta.help}>
      <span style={{ cursor: "help" }}>{tag}</span>
    </Tooltip>
  ) : (
    tag
  );
};

export const LogisticsStatusTag: React.FC<{ status?: string | null }> = ({ status }) => (
  <WordTag value={status} meta={status ? STATUS_META[status] : undefined} />
);

// The clean 5-state delivery signal — the tag the board and booking header lead with.
export const DeliveryStatusTag: React.FC<{ status?: string | null }> = ({ status }) => (
  <WordTag value={status} meta={status ? DELIVERY_META[status] : undefined} />
);

export const LogisticsStateTag: React.FC<{ state?: string | null }> = ({ state }) => (
  <WordTag value={state} meta={state ? STATE_META[state] : undefined} />
);
